import {
  ActiveJobDisplay,
  DriverModel,
  DriverSearchResultModel,
  JobDriverModel,
  JobModel,
  JobSearchResultModel,
  JobStatusUpdateAndProgress,
  JobTemplateModel,
  NetworkConditionModel,
  ShiftModel,
} from '@fleet/model';
import { DateTime } from 'luxon';

import { createDateTimeDisplay } from './date-time-display.utilities';

export function jobModelToJobTemplate(job: JobModel): JobTemplateModel {
  const template: JobTemplateModel = {
    travellerId: job.traveller ? job.traveller.travellerId : null,
    serviceClass: job.jobDetail.serviceClass,
    serviceClassId: job.jobDetail.serviceClassId,
    serviceLine: job.jobDetail.serviceLine,
    serviceLineId: job.jobDetail.serviceLineId,
    networkServiceId: job.jobDetail.networkServiceId,
    pricingMethodType: job.jobDetail.pricingMethod, //This is now on the kob DETAIL level - FW-1610
    organisationId: job.organisation ? job.organisation.organisationId : null,
    organisationGroupId:
      job.organisation && job.organisation.organisationGroupId
        ? job.organisation.organisationGroupId
        : null,

    startLocation: job.jobDetail.startLocation,
    endLocation: job.jobDetail.endLocation,
    internalInstructions: job.jobDetail.internalInstructions,
    phoneNumber: job.traveller ? job.traveller.contactNumber : null,

    conditions: job.jobDetail.conditions
      ? job.jobDetail.conditions.map((s) => {
          return { networkConditionId: s.networkConditionId };
        })
      : [],

    primaryTravellerProfileId: job.traveller
      ? job.traveller.travellerProfileId
      : null,
  } as any;

  if (template.endLocation && !template.endLocation.latitude) {
    delete template['endLocation'];
  }

  return template;
}

export function nextSLA(now: DateTime, sla: DateTime) {
  const dif = sla.diff(now, ['minutes', 'seconds']).toObject();
  let time = '';
  if (dif.minutes > 0) {
    time += dif.minutes + 'm ';
  }
  time += Math.round(dif.seconds) + 's';
  return 'Next ' + time;
}

export function jobModelFromJobSearchResultModel(
  searchResult: JobSearchResultModel
) {
  return {
    jobStatus: searchResult.status,
    jobDetail: {
      startLocation: {
        latitude: searchResult.startLatitude,
        longitude: searchResult.startLongitude,
      },
      endLocation: {
        latitude: searchResult.endLatitude,
        longitude: searchResult.endLongitude,
      },
    },
  } as JobModel;
}

export const jobSearchResultFromJobModel = (
  job: JobModel
): JobSearchResultModel => {
  return {
    jobId: job.jobId,
    status: job.jobStatus,
    startLatitude: job.jobDetail.startLocation?.latitude,
    startLongitude: job.jobDetail.startLocation?.longitude,
    endLatitude: job.jobDetail.endLocation?.latitude,
    endLongitude: job.jobDetail.endLocation?.longitude,
    startTime: job.jobDetail.startTime, // Assuming startTime is a DateTime object
    endTime: job.jobDetail.endTime,
    plateNumber: job.vehicle?.plate.plateNumber,
    travellerFirstName: job.traveller?.name.split(' ')[0],
    travellerLastName: job.traveller?.name.split(' ')?.slice(1)?.join(' '),
    networkConditionIds: job.jobDetail?.conditions
      ? job.jobDetail.conditions.map(
          (cond: NetworkConditionModel) => cond.networkConditionId
        )
      : [],
  } as any;
};

export function jobModelFromJobStatusUpdateAndProgress(
  job: JobStatusUpdateAndProgress
) {
  return {
    ...jobModelFromJobSearchResultModel(job.latestStatusUpdate),
    jobProgress: job.latestJobProgress,
  } as JobModel;
}

export function driverSearchResultToJobDriver(dsr: DriverSearchResultModel) {
  return {
    driverId: dsr.driverId,
    name: dsr.displayName,
    contactNumber: dsr.phoneNumber,
    photoUrl: dsr.imageUrl,
    rating: null,
    taxiAuthorityNumber: dsr.taxiAuthorityNumber,
  } as JobDriverModel;
}

export function driverModelToJobDriver(driver: DriverModel) {
  return {
    driverId: driver.driverId,

    name: driver.fullName || driver.firstName,
    contactNumber: driver.phoneNumber,
    rating: null,
    photoUrl: driver.driverPhoto?.url,
    taxiAuthorityNumber: driver.taxiAuthorityNumber,
  } as JobDriverModel;
}

export function isJobStartwithinShift(job: JobModel, shift: ShiftModel) {
  let shiftStart = shift.scheduledStart;
  if (shift.start) {
    shiftStart = shift.start;
  }
  let shiftEnd = shift.scheduledEnd;
  if (shift.end) {
    shiftEnd = shift.end;
  }
  let jobStartTime = job.jobDetail.startTime;
  if (jobStartTime < DateTime.now()) {
    jobStartTime = DateTime.now();
  }

  if (typeof shiftEnd === 'string') {
    shiftEnd = DateTime.fromISO(shiftEnd);
  }
  if (typeof shiftStart === 'string') {
    shiftStart = DateTime.fromISO(shiftStart);
  }
  if (typeof jobStartTime === 'string') {
    jobStartTime = DateTime.fromISO(jobStartTime);
  }

  if (jobStartTime > shiftStart && jobStartTime < shiftEnd) {
    return { withinShift: true, withinExtendedShift: false };
  } else if (
    jobStartTime &&
    shiftEnd &&
    jobStartTime > shiftStart &&
    jobStartTime < shiftEnd.plus({ hours: 2 })
  ) {
    return { withinShift: false, withinExtendedShift: true };
  }
  return { withinShift: false, withinExtendedShift: false };
}

export function isJobExpired(status: string | undefined | null): boolean {
  return status === 'EXPIRED';
}

export function isJobAbandoned(status: string | undefined | null): boolean {
  return status === 'ABANDONED';
}

export function isJobCancelledStatus(
  status: string | undefined | null
): boolean {
  return status === 'CANCELLED';
}

export type VisitTerminalDisplayStatus =
  | 'EXPIRED'
  | 'ABANDONED'
  | 'CANCELLED'
  | 'COMPLETED'
  | null;

export type VisitCompletionActuals = {
  actualArrival?: DateTime | string | null;
  actualDeparture?: DateTime | string | null;
};

/** True when the visit is marked COMPLETED or has a recorded actual time. */
export function isVisitCompleted(
  visit:
    | ({ status?: string | null } & VisitCompletionActuals)
    | undefined
    | null
): boolean {
  if (!visit) {
    return false;
  }
  return (
    visit.status === 'COMPLETED' ||
    !!visit.actualDeparture ||
    !!visit.actualArrival
  );
}

/** Terminal display state for a visit — job status wins over visit.status. */
export function resolveVisitTerminalDisplayStatus(
  visitStatus: string | undefined,
  jobStatus: string | undefined,
  actuals?: VisitCompletionActuals
): VisitTerminalDisplayStatus {
  if (isJobExpired(jobStatus)) {
    return 'EXPIRED';
  }
  if (isJobAbandoned(jobStatus)) {
    return 'ABANDONED';
  }
  if (
    visitStatus === 'COMPLETED' ||
    !!actuals?.actualDeparture ||
    !!actuals?.actualArrival
  ) {
    return 'COMPLETED';
  }
  if (isJobCancelledStatus(jobStatus) || visitStatus === 'CANCELLED') {
    return 'CANCELLED';
  }
  return null;
}

export function jobColorFromStatus(status: string) {
  switch (status) {
    case 'OPEN':
    case 'ACKNOWLEDGEMENT_REQUIRED':
      return { badgeColor: 'gray', borderColor: 'border-gray' };

    case 'ABANDONED':
      return { badgeColor: 'orange-500', borderColor: 'border-orange-500' };
    case 'DISPATCHED':
      return { badgeColor: 'amber-500', borderColor: 'border-amber-500' };
    case 'ASSIGNED':
      return { badgeColor: 'emerald-500', borderColor: 'border-emerald-500' };
    case 'STARTED':
      return { badgeColor: 'green-500', borderColor: 'border-green-500' };
    case 'IN_PROGRESS':
      return { badgeColor: 'lime-500', borderColor: 'border-lime-500' };
    case 'FINISHED':
      return { badgeColor: 'blue-500', borderColor: 'border-blue-500' };
    case 'EXPIRED':
    case 'CANCELLED':
      return { badgeColor: 'red-600', borderColor: 'border-red-600' };
    default:
      return { badgeColor: 'gray', borderColor: 'border-gray' };
  }
}

export function createActiveJobDisplay(
  job: JobStatusUpdateAndProgress
): ActiveJobDisplay {
  const display = {} as ActiveJobDisplay;

  display.startAddressDisplay = createJobSearchResultToAddressDisplay(
    job.latestStatusUpdate,
    'START'
  );
  display.endAddressDisplay = createJobSearchResultToAddressDisplay(
    job.latestStatusUpdate,
    'END'
  );

  if (job.latestJobProgress?.activeLocation) {
    display.jobStatus = job.latestJobProgress?.activeLocation.status;
  } else {
    display.jobStatus = job.latestStatusUpdate?.status;
  }

  display.activeLocationLine1 =
    job.latestJobProgress?.activeLocation?.displayLine1;
  display.activeLocationLine2 =
    job.latestJobProgress?.activeLocation?.displayLine2;
  const slaStartTime = job.latestStatusUpdate?.dispatchSlaStartTime
    ? typeof job.latestStatusUpdate.dispatchSlaStartTime === 'string'
      ? DateTime.fromISO(job.latestStatusUpdate.dispatchSlaStartTime)
      : job.latestStatusUpdate.dispatchSlaStartTime
    : null;

  const timeDisplay = createDateTimeDisplay(
    slaStartTime,
    null,
    null,
    true,
    null
  );
  display.slaStartTime = timeDisplay.startTimeSimple;
  display.slaStartTimeAMPM = timeDisplay.startTimeAMPM;

  display.slaLevel = 0;
  display.outerRingStroke = job.statusColor?.badgeColor;
  display.slaColor = 'gray-500';
  display.startLocality = job.latestStatusUpdate?.startLocality;
  display.endLocality = job.latestStatusUpdate?.endLocality;
  display.jobId = job.latestStatusUpdate?.jobId;
  display.networkConditionIds =
    job.latestStatusUpdate?.networkConditionIds ?? [];
  display.status = job.latestStatusUpdate?.status ?? '';
  display.eta = job.latestJobProgress?.displayEta;
  display.distance = job.latestJobProgress?.distance?.displayValue;
  display.travellerFirstName = job.latestStatusUpdate?.travellerFirstName;
  display.travellerLastName = job.latestStatusUpdate?.travellerLastName;
  display.organisationName = job.latestStatusUpdate?.organisationName;
  display.driverFirstName = job.latestStatusUpdate?.driverFirstName;
  // display.driverTaxiAuthorityNumber =
  //   job.latestStatusUpdate?.driverAuthorityNumber;
  display.plateNumber = job.latestStatusUpdate?.plateNumber;
  display.medallionNumber = job.latestStatusUpdate?.medallionNumber;
  display.numberOfTravellers = job.latestStatusUpdate?.numberOfTravellers;
  display.startAddress =
    (job.latestStatusUpdate?.startUnitNumber
      ? job.latestStatusUpdate?.startUnitNumber + '/'
      : '') +
    ' ' +
    job.latestStatusUpdate?.startStreetNumber +
    ' ' +
    job.latestStatusUpdate?.startStreetName;

  display.endAddress =
    (job.latestStatusUpdate?.endUnitNumber
      ? job.latestStatusUpdate?.endUnitNumber + '/'
      : '') +
    job.latestStatusUpdate?.endStreetNumber +
    ' ' +
    job.latestStatusUpdate?.endStreetName;

  const now = DateTime.now().setZone(slaStartTime.zone, {
    keepLocalTime: true,
  });
  const dispatchSlaLevelThree = job.latestStatusUpdate?.dispatchSlaLevelThree
    ? DateTime.fromISO(
        job.latestStatusUpdate?.dispatchSlaLevelThree?.toString()
      )
    : null;
  display.millisecondsTillSLA3 = dispatchSlaLevelThree
    ? now.diff(dispatchSlaLevelThree).toMillis()
    : null;

  if (now > slaStartTime) {
    const dispatchSlaLevelTwo = job.latestStatusUpdate?.dispatchSlaLevelTwo
      ? DateTime.fromISO(
          job.latestStatusUpdate?.dispatchSlaLevelTwo?.toString()
        )
      : null;
    const dispatchSlaLevelOne = job.latestStatusUpdate?.dispatchSlaLevelOne
      ? DateTime.fromISO(
          job.latestStatusUpdate?.dispatchSlaLevelOne?.toString()
        )
      : null;

    if (now > dispatchSlaLevelThree) {
      display.slaLevel = 4;
      display.slaColor = 'red-500';

      const minutes = Math.round(
        now.diff(slaStartTime, 'minutes').toObject().minutes
      );
      display.timeUntilNextSla = `Late ${minutes}min`;
    } else if (now > dispatchSlaLevelTwo) {
      display.slaLevel = 3;
      display.timeUntilNextSla = nextSLA(now, dispatchSlaLevelThree);
      display.slaColor = 'red-500';
    } else if (now > dispatchSlaLevelOne) {
      display.slaLevel = 2;
      display.timeUntilNextSla = nextSLA(now, dispatchSlaLevelTwo);
      display.slaColor = 'orange-500';
    } else {
      display.slaLevel = 1;
      display.timeUntilNextSla = nextSLA(now, dispatchSlaLevelOne);
      display.slaColor = 'emerald-500';
    }
  }

  return display;
}

export function createJobSearchResultToAddressDisplay(
  job: JobSearchResultModel | any,
  type: string,
  singleLine?: boolean,
  html?: boolean
): any {
  if (job && type) {
    //en
    if (html) {
      if (type === 'START') {
        let addressHtml = '';

        if (job.startPlaceName) {
          addressHtml += job.startPlaceName + ', ';
        }
        if (job.startStreetName) {
          addressHtml +=
            (job.startStreetNumber ? job.startStreetNumber + ' ' : '') +
            job.startStreetName +
            ', ';
        }
        if (job.startLocality !== job.startPlaceName) {
          addressHtml += job.startLocality.toUpperCase();
        }
        return addressHtml;
      } else {
        //END
        let addressHtml = '';

        if (job.endPlaceName) {
          if (job.endPlaceName === 'As Directed') {
            return 'AS DIRECTED';
          }
          addressHtml += job.endPlaceName + ', ';
        }
        if (job.endStreetName) {
          addressHtml +=
            (job.endStreetNumber ? job.endStreetNumber + ' ' : '') +
            job.endStreetName +
            ', ';
        }
        if (
          job.endLocality !== job.endPlaceName &&
          job.endLocality !== 'Unknown'
        ) {
          addressHtml += job.endLocality.toUpperCase();
        }
        return addressHtml;
      }
    }

    const address = { displayLine1: '', displayLine2: '' };
    if (type === 'END') {
      if (job.endLocality === 'Unknown') {
        return singleLine ? 'As Directed' : { displayLine1: 'As Directed' };
      } else {
        if (job.endPlaceName && job.endPlaceName !== job.endLocality) {
          address.displayLine1 = job.endPlaceName;
        }
        if (
          !job.endPlaceName &&
          job.endStreetName &&
          job.endStreetName !== job.endLocality
        ) {
          address.displayLine1 =
            (job.endStreetNumber ? job.endStreetNumber + ' ' : '') +
            job.endStreetName;
        }
        address.displayLine2 = job.endLocality;
      }
    } else {
      //start
      if (job.startPlaceName) {
        address.displayLine1 = job.startPlaceName;
      }
      if (!job.startPlaceName && job.startStreetName) {
        address.displayLine1 =
          (job.startStreetNumber ? job.startStreetNumber + ' ' : '') +
          job.startStreetName;
      }
      if (job.startLocality !== job.startPlaceName) {
        address.displayLine1 = address.displayLine1 ? address.displayLine1 : '';
        address.displayLine2 = job.startLocality;
      }
    }
    return singleLine
      ? address.displayLine1 + ' ' + address.displayLine2
      : address;
  }

  return null;
}

type WillCallJobInput = JobSearchResultModel | JobModel | null | undefined;

function hasScheduledTime(
  value: DateTime | string | null | undefined
): boolean {
  if (value == null || value === '') {
    return false;
  }
  if (DateTime.isDateTime(value)) {
    return value.isValid;
  }
  if (typeof value === 'string') {
    return DateTime.fromISO(value).isValid;
  }
  return true;
}

function willCallJobFields(job: WillCallJobInput): {
  willCall: boolean;
  pickupTime: DateTime | string | null | undefined;
  completionTime: DateTime | string | null | undefined;
} | null {
  if (!job) {
    return null;
  }

  if ('jobDetail' in job && job.jobDetail) {
    return {
      willCall: job.jobDetail.willCall === true,
      pickupTime: job.jobDetail.startTime,
      completionTime: job.jobDetail.endTime,
    };
  }

  const searchResult = job as JobSearchResultModel;
  return {
    willCall: searchResult.willCall === true,
    pickupTime: searchResult.requestedPickup,
    completionTime: searchResult.requestedCompletion,
  };
}

/** True when the job is an active/pending will-call (flag set, no pickup or completion time). */
export function isWillCallJob(job: WillCallJobInput): boolean {
  const fields = willCallJobFields(job);
  if (!fields?.willCall) {
    return false;
  }
  if (hasScheduledTime(fields.pickupTime)) {
    return false;
  }
  if (hasScheduledTime(fields.completionTime)) {
    return false;
  }
  return true;
}

export function convertJobDateTimesToDateTime(
  job: JobSearchResultModel
): JobSearchResultModel {
  const convertedJob = { ...job };

  // Convert datetime strings to DateTime objects if they're strings
  if (
    convertedJob.expectedPickup &&
    typeof convertedJob.expectedPickup === 'string'
  ) {
    convertedJob.expectedPickup = DateTime.fromISO(convertedJob.expectedPickup);
  }

  if (
    convertedJob.expectedCompletion &&
    typeof convertedJob.expectedCompletion === 'string'
  ) {
    convertedJob.expectedCompletion = DateTime.fromISO(
      convertedJob.expectedCompletion,
      { setZone: true }
    );
  }

  if (
    convertedJob.requestedPickup &&
    typeof convertedJob.requestedPickup === 'string'
  ) {
    convertedJob.requestedPickup = DateTime.fromISO(
      convertedJob.requestedPickup,
      { setZone: true }
    );
  }

  if (
    convertedJob.requestedCompletion &&
    typeof convertedJob.requestedCompletion === 'string'
  ) {
    convertedJob.requestedCompletion = DateTime.fromISO(
      convertedJob.requestedCompletion,
      { setZone: true }
    );
  }

  return convertedJob;
}
